import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import {
  ArrowRight,
  ArrowUpRight,
  Sparkles,
  Heart,
  Users,
  Home as HomeIcon,
  Puzzle,
  GraduationCap,
  Briefcase,
  Brain,
  Activity,
  CloudRain,
  Compass,
  Shield,
  Flame,
  HandHeart,
  Anchor,
  TrendingUp,
  Instagram,
  Play,
  CheckCircle2,
  Plus,
  Minus,
  ChevronRight,
  HelpCircle,
  Wind,
  Mountain,
  Sunrise,
  Leaf,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BOOKING_URL = "https://sophia-dinwiddie-donald.clientsecure.me/request/clinician";

const HERO_BLOB = "https://static.prod-images.emergentagent.com/jobs/45c13cc0-c545-4ce4-a5ad-55fc17f653c4/images/e1e8769e8a9dbbc666c6f7de616b7e45326e50907109e5409d39067d930dc3fe.png";
const GIFT_ILLO = "https://static.prod-images.emergentagent.com/jobs/45c13cc0-c545-4ce4-a5ad-55fc17f653c4/images/f2a7f080106bb6ddda61a36a1d6129a29653cb3d730675490e6620ff96925554.png";

const SOPHIA_PHOTO = "/team/sophia-dinwiddie-donald.png";
const ASHAKI_PHOTO = "/team/ashaki-gilmer.jpg";

const SOFT_HOVER = "transition-all duration-200 hover:-translate-y-0.5";

// ============ DATA ============

const services = [
  {
    key: "individual",
    icon: Heart,
    title: "Individual Therapy",
    punch: "Anxiety, overthinking, life pivots.",
    detail: "1:1 support tailored to you. Anxiety, dating burnout, life transitions, identity work. Virtual sessions across Texas.",
    to: "/services",
  },
  {
    key: "couples",
    icon: Users,
    title: "Couples Therapy",
    punch: "Communication. Trust. Reconnection.",
    detail: "Repair work for partners ready to do the actual work. Communication, repair after rupture, intimacy, and shared values.",
    to: "/services",
  },
  {
    key: "family",
    icon: HomeIcon,
    title: "Family Therapy",
    punch: "Healthier dynamics, real change.",
    detail: "For families navigating transition, conflict, neurodivergence, or generational patterns. We move at your pace.",
    to: "/services",
  },
  {
    key: "autism",
    icon: Puzzle,
    title: "Autism Life Skills",
    punch: "Identity-affirming. Never masking.",
    detail: "Practical life-skills coaching for autistic adults — executive function, sensory regulation, workplace navigation, communication. Built around how you actually work.",
    to: "/autism-life-skills",
    spotlight: true,
  },
  {
    key: "supervision",
    icon: GraduationCap,
    title: "LCSW Supervision",
    punch: "Texas LMSWs → LCSWs.",
    detail: "Structured, ethical, growth-focused supervision for Texas LMSWs moving toward independent licensure.",
    to: "/lcsw-supervision",
  },
  {
    key: "internship",
    icon: Briefcase,
    title: "Internship Program",
    punch: "Real clinical training.",
    detail: "Field placement and MSW internship opportunities for emerging clinicians — real cases, real supervision, real growth.",
    to: "/services",
  },
];

const specialties = [
  { icon: Activity, label: "Anxiety" },
  { icon: CloudRain, label: "Depression" },
  { icon: Brain, label: "ADHD" },
  { icon: Puzzle, label: "Autism" },
  { icon: Heart, label: "Dating" },
  { icon: Compass, label: "Transitions" },
  { icon: Shield, label: "Boundaries" },
  { icon: Flame, label: "Burnout" },
];

const pillars = [
  { n: "01", icon: HandHeart, name: "Compassion", line: "Safe. Nonjudgmental. Seen.", deeper: "We meet you where you actually are — not where you think you should be. No fixing, no rushing." },
  { n: "02", icon: Sparkles, name: "Confidence", line: "Trust yourself again.", deeper: "We rebuild the inner voice that knows. Therapy that grows your trust in your own judgment." },
  { n: "03", icon: Brain, name: "Insight", line: "Patterns get clearer.", deeper: "Why you keep choosing the same partner, the same job, the same loop — and how it can change." },
  { n: "04", icon: Anchor, name: "Resilience", line: "Cope. Adapt. Recover.", deeper: "Practical regulation skills your nervous system can use in real life — not just in session." },
  { n: "05", icon: Compass, name: "Alignment", line: "Live your values.", deeper: "Surface what you actually care about. Build a life that runs on your compass, not someone else's." },
  { n: "06", icon: TrendingUp, name: "Growth", line: "Your pace, not theirs.", deeper: "Real change is slow. We honor your timeline and protect it from the culture that wants you sprinting." },
  { n: "07", icon: Users, name: "Belonging", line: "You're not alone.", deeper: "Belonging starts inside, then ripples outward. We work on both — together, in real time." },
];

const stats = [
  { n: "100+", label: "Supervision hours pathway" },
  { n: "5", label: "Day reflection guide" },
  { n: "1:1", label: "Virtual sessions, statewide" },
  { n: "TX", label: "Licensed across Texas" },
];

const team = [
  {
    name: "Sophia Dinwiddie-Donald",
    creds: "MSSW, LCSW-S",
    role: "Founder & Clinical Leader",
    image: SOPHIA_PHOTO,
    blurb: "Therapy, clinical supervision, and program leadership focused on life transitions, relationships, and growth.",
  },
  {
    name: "Ashaki Gilmer",
    creds: "Clinical Director",
    role: "Leadership & Clinical Support",
    image: ASHAKI_PHOTO,
    blurb: "Brings warmth, structure, and dedication — guiding our team in ethical, compassionate, well-supported care.",
  },
];

// "Choose what fits your season" pathway picker
const seasons = [
  {
    key: "anxious",
    icon: Wind,
    label: "Anxious",
    sub: "Mind won't stop.",
    headline: "Therapy for an overthinking mind.",
    body: "Practical tools to slow the spiral, regulate your nervous system, and stop renting your brain to worry.",
    cta: { label: "Start with Individual Therapy", to: "/services" },
  },
  {
    key: "transition",
    icon: Sunrise,
    label: "In transition",
    sub: "Everything's shifting.",
    headline: "For seasons that don't have a script.",
    body: "Breakup, divorce, career pivot, identity shift, new chapter — we help you choose your own next step.",
    cta: { label: "Explore the blog", to: "/blog" },
  },
  {
    key: "healing",
    icon: Leaf,
    label: "Healing",
    sub: "Working through it.",
    headline: "Slow, structured, real healing.",
    body: "After a breakup, betrayal, family rupture, or a season that broke you open — we build the floor back under your feet.",
    cta: { label: "Read post-breakup life skills", to: "/blog/life-skills-after-a-breakup-rebuilding-your-routine-confidence-and-peace" },
  },
  {
    key: "growing",
    icon: Mountain,
    label: "Growing",
    sub: "Leveling up.",
    headline: "When you're ready to grow on purpose.",
    body: "Clinical supervision, life-skills coaching, identity-affirming work for neurodivergent adults — the next layer.",
    cta: { label: "See supervision + coaching", to: "/lcsw-supervision" },
  },
];

// 3-question reflection quiz
const quiz = [
  {
    id: "q1",
    label: "What's getting in the way most right now?",
    options: [
      { value: "mind", text: "My own thoughts — anxiety, overthinking, doubt" },
      { value: "relationship", text: "Relationships — dating, breakup, family, partner" },
      { value: "transition", text: "A life transition — breakup, divorce, career, identity" },
      { value: "growth", text: "I want to level up — clinical, professional, personal" },
    ],
  },
  {
    id: "q2",
    label: "How do you process best?",
    options: [
      { value: "talk", text: "Talking it through with someone who gets it" },
      { value: "tools", text: "Practical tools and structure I can use day-to-day" },
      { value: "slow", text: "Slow, reflective space — no pressure" },
    ],
  },
  {
    id: "q3",
    label: "What would 'better' look like in 6 months?",
    options: [
      { value: "calm", text: "Calmer mind, more steady mood" },
      { value: "clear", text: "Clearer choices, more aligned life" },
      { value: "connected", text: "Better connection with myself or others" },
    ],
  },
];

function quizRecommendation(answers) {
  if (!answers.q1) return null;
  const map = {
    mind: {
      title: "Individual Therapy.",
      body: "1:1 sessions to slow the spiral, regulate your nervous system, and rebuild trust in your own thinking.",
      ctas: [
        { label: "Book a Consult", to: BOOKING_URL, external: true, primary: true },
        { label: "Free reflection guide", to: "#free-guide", external: false },
      ],
    },
    relationship: {
      title: "Couples or Individual Therapy.",
      body: "We work on the patterns underneath — attachment, communication, repair, and what you actually want from love.",
      ctas: [
        { label: "Book a Consult", to: BOOKING_URL, external: true, primary: true },
        { label: "Read on relationship patterns", to: "/blog/relationship-patterns-why-you-keep-choosing-the-same-type-of-person", external: false },
      ],
    },
    transition: {
      title: "Therapy for life transitions.",
      body: "We help you choose your next step on purpose — not on autopilot. Breakups, divorce, career, identity, all of it.",
      ctas: [
        { label: "Book a Consult", to: BOOKING_URL, external: true, primary: true },
        { label: "Read divorce + breakup posts", to: "/blog", external: false },
      ],
    },
    growth: {
      title: "Supervision or Life-Skills Coaching.",
      body: "Whether you're moving toward LCSW licensure or building life skills as a neurodivergent adult — we structure the path forward.",
      ctas: [
        { label: "LCSW Supervision", to: "/lcsw-supervision", external: false, primary: true },
        { label: "Autism Life Skills", to: "/autism-life-skills", external: false },
      ],
    },
  };
  return map[answers.q1] || null;
}

const faqs = [
  {
    q: "Are sessions virtual or in person?",
    a: "All sessions are virtual and available to anyone in Texas. No commute, no waiting rooms — just a quiet conversation from a space that feels safe.",
  },
  {
    q: "How do I know if you're the right therapist for me?",
    a: "That's exactly what the free 15-minute consult is for. You ask questions, we listen, and you decide if it feels like a fit. No pressure, no commitment.",
  },
  {
    q: "What's the first session actually like?",
    a: "Mostly conversation. We get a sense of what's bringing you in, what you've already tried, and what you're hoping for. No deep-dives until you're ready.",
  },
  {
    q: "Do you take insurance?",
    a: "We're a private-pay practice — which means more flexibility, more privacy, and no diagnosis attached to your record. We can provide a superbill for out-of-network reimbursement.",
  },
  {
    q: "How long does therapy take?",
    a: "It depends on what you're working through. Some people come for a few months around a specific transition; others stay longer for deeper work. We'll talk about pace together.",
  },
  {
    q: "Can I switch from therapy to supervision (or vice versa)?",
    a: "Not within the same relationship — but we can absolutely refer you to a trusted colleague if your needs shift. The team here is collaborative.",
  },
];

// ============ COMPONENTS ============

function ServiceTile({ s, expanded, onToggle }) {
  const Icon = s.icon;
  return (
    <article
      data-testid={`service-card-${s.key}`}
      className={`group ${s.spotlight ? "bg-terracotta text-bone rounded-3xl p-8 md:p-10" : "p-1"} ${SOFT_HOVER}`}
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`service-toggle-${s.key}`}
        className="text-left w-full"
      >
        <div className={`inline-flex h-14 w-14 items-center justify-center rounded-full ${s.spotlight ? "bg-bone/15 text-bone" : "bg-surface text-forest group-hover:bg-terracotta group-hover:text-bone"} mb-6 transition-colors`}>
          <Icon size={24} strokeWidth={2} />
        </div>
        <div className="flex items-start justify-between gap-4">
          <h3 className={`font-serif font-bold text-3xl md:text-4xl leading-tight ${s.spotlight ? "text-bone" : "text-forest group-hover:text-terracotta transition-colors"}`}>{s.title}</h3>
          <span className={`shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-full border ${s.spotlight ? "border-bone/40 text-bone" : "border-forest/20 text-forest/70 group-hover:border-terracotta group-hover:text-terracotta"} transition-colors`}>
            {expanded ? <Minus size={16} strokeWidth={2.2} /> : <Plus size={16} strokeWidth={2.2} />}
          </span>
        </div>
        <p className={`mt-3 text-base md:text-lg font-medium ${s.spotlight ? "text-bone/90" : "text-forest/70"}`}>{s.punch}</p>
      </button>

      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${expanded ? "grid-rows-[1fr] mt-5" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden">
          <p className={`text-base leading-relaxed ${s.spotlight ? "text-bone/85" : "text-forest/75"}`}>{s.detail}</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to={s.to}
              data-testid={`service-learn-${s.key}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-bold uppercase tracking-wider text-xs ${s.spotlight ? "bg-bone text-forest hover:bg-forest hover:text-bone" : "bg-forest text-bone hover:bg-terracotta"} ${SOFT_HOVER}`}
            >
              Learn more <ArrowUpRight size={14} strokeWidth={2.5} />
            </Link>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`service-book-${s.key}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-bold uppercase tracking-wider text-xs border ${s.spotlight ? "border-bone text-bone hover:bg-bone hover:text-forest" : "border-forest text-forest hover:bg-forest hover:text-bone"} ${SOFT_HOVER}`}
            >
              Book a Consult
            </a>
          </div>
        </div>
      </div>
    </article>
  );
}

function PillarTile({ p, isActive, onClick }) {
  const Icon = p.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`pillar-card-${p.name.toLowerCase()}`}
      className={`group text-left px-6 py-7 rounded-2xl transition-all duration-200 ${isActive ? "bg-bone shadow-md" : "hover:bg-bone/60"}`}
    >
      <p className={`font-serif font-black text-3xl leading-none ${isActive ? "text-terracotta" : "text-terracotta/80"}`}>{p.n}</p>
      <Icon size={20} strokeWidth={2.2} className={`my-3 ${isActive ? "text-terracotta" : "text-forest group-hover:text-terracotta transition-colors"}`} />
      <p className="font-black text-sm uppercase tracking-wide text-forest">{p.name}</p>
      <p className="mt-1.5 text-xs font-medium text-forest/65 leading-snug">{p.line}</p>
    </button>
  );
}

function FAQItem({ q, a, idx, open, onToggle }) {
  return (
    <div className={`border-t border-forest/15 ${idx === faqs.length - 1 ? "border-b" : ""}`}>
      <button
        type="button"
        onClick={onToggle}
        data-testid={`faq-toggle-${idx}`}
        className="w-full text-left py-6 flex items-start justify-between gap-6"
      >
        <span className="font-serif font-bold text-xl md:text-2xl text-forest leading-snug">{q}</span>
        <span className={`shrink-0 mt-1 inline-flex h-9 w-9 items-center justify-center rounded-full border border-forest/20 text-forest/70 transition-transform ${open ? "rotate-45 bg-terracotta text-bone border-terracotta" : ""}`}>
          <Plus size={18} strokeWidth={2.2} />
        </span>
      </button>
      <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr] pb-6" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <p data-testid={`faq-body-${idx}`} className="text-forest/75 leading-relaxed max-w-3xl">{a}</p>
        </div>
      </div>
    </div>
  );
}

// ============ MAIN PAGE ============

export default function Explore() {
  const [email, setEmail] = useState("");
  const [first, setFirst] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [openService, setOpenService] = useState(null);
  const [activePillar, setActivePillar] = useState("01");
  const [activeSeason, setActiveSeason] = useState("anxious");
  const [quizAnswers, setQuizAnswers] = useState({});
  const [openFaq, setOpenFaq] = useState(null);

  const onLeadSubmit = async (e) => {
    e.preventDefault();
    if (!email || !first) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/lead-magnet/signup`, { first_name: first, last_name: "", email });
      toast.success("Your guide is on the way.");
      setSuccess(true);
      setEmail(""); setFirst("");
    } catch (err) {
      toast.error("Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const seasonObj = seasons.find((s) => s.key === activeSeason);
  const pillarObj = pillars.find((p) => p.n === activePillar);
  const recommendation = quizRecommendation(quizAnswers);
  const quizDone = !!quizAnswers.q1 && !!quizAnswers.q2 && !!quizAnswers.q3;

  return (
    <div data-testid="explore-page" className="bg-bone text-forest overflow-hidden">
      <SEO
        title="Therapy that meets you where you actually are — Seven Pillars"
        description="Bold, modern therapy in Texas. Anxiety, dating, supervision, autism life skills, life transitions. By Sophia Dinwiddie-Donald, LCSW-S."
        path="/explore"
      />

      {/* CONCEPT BANNER */}
      <div data-testid="explore-banner" className="bg-forest text-bone text-center py-2.5 text-xs font-bold uppercase tracking-[0.25em]">
        ✦ Concept page · Same Seven Pillars, bolder vibe ·{" "}
        <Link to="/" className="underline underline-offset-4 hover:text-terracotta transition-colors">Back to current site</Link>
      </div>

      {/* HERO */}
      <section className="relative pt-20 pb-28 md:pt-28 md:pb-36 px-6 md:px-12" data-testid="explore-hero">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-7 relative z-10">
            <p className="inline-flex items-center gap-2 mb-7 text-xs font-bold uppercase tracking-[0.28em] text-terracotta">
              <Sparkles size={12} strokeWidth={2.5} /> Therapy in Texas · Real Talk
            </p>
            <h1 className="font-serif font-black leading-[0.95] tracking-tighter text-5xl sm:text-6xl lg:text-[88px]">
              You're <span className="italic text-terracotta">not</span> behind. <br />
              You're <span className="italic text-terracotta">in transition.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-forest/80 leading-relaxed max-w-xl">
              Bold, kind therapy for the patterns that quietly run your life. Anxiety, dating, breakups, supervision, autism life skills — and the in-between.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="explore-hero-cta"
                className={`inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-bone rounded-full px-8 py-4 font-bold uppercase tracking-wider text-sm ${SOFT_HOVER}`}
              >
                Book A Consult <ArrowRight size={18} strokeWidth={2.5} />
              </a>
              <a
                href="#season-picker"
                data-testid="explore-hero-quiz-cta"
                className={`inline-flex items-center gap-2 bg-transparent hover:bg-forest hover:text-bone border border-forest text-forest rounded-full px-8 py-4 font-bold uppercase tracking-wider text-sm ${SOFT_HOVER}`}
              >
                What's Your Season? <Sparkles size={16} strokeWidth={2.5} />
              </a>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs font-bold uppercase tracking-[0.22em] text-forest/65">
              <span className="flex items-center gap-2"><CheckCircle2 size={14} strokeWidth={2.5} className="text-terracotta" /> Texas Licensed</span>
              <span className="flex items-center gap-2"><CheckCircle2 size={14} strokeWidth={2.5} className="text-terracotta" /> Virtual statewide</span>
              <span className="flex items-center gap-2"><CheckCircle2 size={14} strokeWidth={2.5} className="text-terracotta" /> Real humans</span>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-[2rem] overflow-hidden aspect-square bg-surface">
              <img src={HERO_BLOB} alt="Calm abstract shapes" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -top-6 -right-4 w-24 h-24 rounded-full bg-terracotta/15 -z-10" aria-hidden="true" />
            <div className="absolute -bottom-8 -left-6 w-32 h-32 rounded-full bg-forest/10 -z-10" aria-hidden="true" />
          </div>
        </div>
      </section>

      {/* SPECIALTIES TICKER */}
      <section data-testid="explore-specialties" className="bg-forest text-bone py-12 md:py-14 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-terracotta mb-5">We help with</p>
          <div className="flex flex-wrap gap-3">
            {specialties.map((s) => {
              const Icon = s.icon;
              return (
                <span
                  key={s.label}
                  data-testid={`specialty-pill-${s.label.toLowerCase()}`}
                  className="inline-flex items-center gap-2 border border-bone/30 hover:border-terracotta hover:bg-terracotta hover:text-bone bg-forest/40 rounded-full px-5 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors duration-200 cursor-default"
                >
                  <Icon size={16} strokeWidth={2.2} /> {s.label}
                </span>
              );
            })}
          </div>
        </div>
      </section>

      {/* SEASON PICKER */}
      <section id="season-picker" className="py-24 md:py-32 px-6 md:px-12" data-testid="explore-season">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-terracotta mb-4">Choose what fits</p>
            <h2 className="font-serif font-black tracking-tight text-4xl md:text-6xl leading-[0.95]">
              What season are <em className="italic text-terracotta">you in?</em>
            </h2>
            <p className="mt-5 text-forest/75 text-lg leading-relaxed">
              Pick the word that fits right now. We'll show you a starting point.
            </p>
          </div>

          {/* Season buttons */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-10">
            {seasons.map((s) => {
              const Icon = s.icon;
              const active = s.key === activeSeason;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setActiveSeason(s.key)}
                  data-testid={`season-btn-${s.key}`}
                  className={`text-left p-5 md:p-6 rounded-2xl border transition-all duration-200 ${
                    active
                      ? "bg-forest text-bone border-forest shadow-lg"
                      : "bg-bone text-forest border-forest/15 hover:border-terracotta hover:-translate-y-0.5"
                  }`}
                >
                  <Icon size={22} strokeWidth={2.2} className={`mb-3 ${active ? "text-terracotta" : "text-forest"}`} />
                  <p className="font-black uppercase tracking-wider text-sm">{s.label}</p>
                  <p className={`mt-1 text-xs font-medium ${active ? "text-bone/70" : "text-forest/55"}`}>{s.sub}</p>
                </button>
              );
            })}
          </div>

          {/* Active season recommendation */}
          {seasonObj && (
            <div
              key={seasonObj.key}
              data-testid={`season-result-${seasonObj.key}`}
              className="rounded-[2rem] bg-surface p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center animate-fadeIn"
              style={{ animation: "fadeIn 0.3s ease-out" }}
            >
              <div className="md:col-span-8">
                <p className="text-xs font-bold uppercase tracking-[0.3em] text-terracotta mb-3">For when you're feeling {seasonObj.label.toLowerCase()}</p>
                <h3 className="font-serif font-black text-3xl md:text-4xl text-forest leading-tight">{seasonObj.headline}</h3>
                <p className="mt-4 text-forest/75 text-lg leading-relaxed">{seasonObj.body}</p>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <Link
                  to={seasonObj.cta.to}
                  data-testid={`season-cta-${seasonObj.key}`}
                  className={`inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-bone rounded-full px-6 py-4 font-bold uppercase tracking-wider text-sm ${SOFT_HOVER}`}
                >
                  {seasonObj.cta.label} <ChevronRight size={16} strokeWidth={2.5} />
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* SERVICES — expandable */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-surface" data-testid="explore-services">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-terracotta mb-4">What we do</p>
              <h2 className="font-serif font-black tracking-tight text-4xl md:text-6xl leading-[0.95]">
                Care that <em className="italic text-terracotta">fits.</em>
              </h2>
              <p className="mt-5 text-forest/70 text-base md:text-lg">Tap any card to read more or book.</p>
            </div>
            <Link to="/services" data-testid="explore-services-all" className="font-bold uppercase tracking-[0.2em] text-sm text-forest underline underline-offset-4 hover:text-terracotta">See all services →</Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {services.map((s) => (
              <ServiceTile
                key={s.key}
                s={s}
                expanded={openService === s.key}
                onToggle={() => setOpenService(openService === s.key ? null : s.key)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* INTERACTIVE 7 PILLARS */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-bone" data-testid="explore-pillars">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-terracotta mb-4">Our foundation</p>
            <h2 className="font-serif font-black tracking-tight text-4xl md:text-6xl leading-[0.95]">
              Seven pillars. <br />
              <em className="italic text-terracotta">Tap to explore.</em>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2">
            {pillars.map((p) => (
              <PillarTile
                key={p.n}
                p={p}
                isActive={activePillar === p.n}
                onClick={() => setActivePillar(p.n)}
              />
            ))}
          </div>

          {pillarObj && (
            <div
              key={pillarObj.n}
              data-testid={`pillar-detail-${pillarObj.name.toLowerCase()}`}
              className="mt-10 max-w-3xl rounded-2xl bg-surface p-7 md:p-10"
            >
              <div className="flex items-center gap-3 mb-3 text-terracotta">
                <span className="font-serif font-black text-2xl">{pillarObj.n}</span>
                <p className="font-black text-sm uppercase tracking-wider text-forest/75">{pillarObj.name}</p>
              </div>
              <p className="font-serif text-xl md:text-2xl text-forest leading-snug">{pillarObj.deeper}</p>
            </div>
          )}
        </div>
      </section>

      {/* STATS STRIP */}
      <section data-testid="explore-stats" className="bg-forest text-bone py-20 md:py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6 md:gap-x-12">
          {stats.map((s) => (
            <div key={s.label} data-testid={`stat-${s.n.toLowerCase()}`} className="text-center md:text-left">
              <p className="font-serif font-black text-5xl md:text-7xl text-terracotta leading-none">{s.n}</p>
              <p className="mt-3 text-xs md:text-sm font-bold uppercase tracking-wider text-bone/70">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* REFLECTION QUIZ */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-bone" data-testid="explore-quiz">
        <div className="max-w-4xl mx-auto">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-terracotta mb-4">3-question reflection</p>
            <h2 className="font-serif font-black tracking-tight text-4xl md:text-6xl leading-[0.95]">
              A starting point, <em className="italic text-terracotta">just for you.</em>
            </h2>
            <p className="mt-5 text-forest/75 text-lg leading-relaxed">
              Three quick questions. No email. No judgment. We'll point you toward what might help.
            </p>
          </div>

          <div className="space-y-8">
            {quiz.map((qq, qi) => (
              <div key={qq.id} data-testid={`quiz-question-${qq.id}`}>
                <p className="font-serif text-2xl md:text-3xl font-bold text-forest leading-snug mb-5">
                  <span className="text-terracotta font-black text-xl mr-2">{qi + 1}.</span>{qq.label}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {qq.options.map((opt) => {
                    const selected = quizAnswers[qq.id] === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        data-testid={`quiz-${qq.id}-${opt.value}`}
                        onClick={() => setQuizAnswers({ ...quizAnswers, [qq.id]: opt.value })}
                        className={`text-left px-5 py-4 rounded-2xl border-2 transition-all duration-200 ${
                          selected
                            ? "border-terracotta bg-terracotta/10 text-forest"
                            : "border-forest/15 hover:border-forest/40 bg-bone hover:-translate-y-0.5"
                        }`}
                      >
                        <span className="flex items-start gap-3">
                          <span className={`shrink-0 inline-flex h-5 w-5 mt-0.5 items-center justify-center rounded-full border-2 ${selected ? "border-terracotta bg-terracotta" : "border-forest/30"}`}>
                            {selected && <CheckCircle2 size={14} strokeWidth={3} className="text-bone" />}
                          </span>
                          <span className="text-base md:text-lg font-medium">{opt.text}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Result */}
          {recommendation && (
            <div
              data-testid="quiz-result"
              className={`mt-12 rounded-[2rem] p-8 md:p-12 transition-all duration-300 ${quizDone ? "bg-forest text-bone" : "bg-surface text-forest"}`}
            >
              <p className={`text-xs font-bold uppercase tracking-[0.3em] mb-4 ${quizDone ? "text-terracotta" : "text-terracotta"}`}>
                {quizDone ? "Your match" : "Starting point"}
              </p>
              <h3 className="font-serif font-black text-3xl md:text-5xl leading-tight">
                {recommendation.title}
              </h3>
              <p className={`mt-5 text-lg leading-relaxed max-w-2xl ${quizDone ? "text-bone/85" : "text-forest/75"}`}>
                {recommendation.body}
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                {recommendation.ctas.map((c) => {
                  const cls = c.primary
                    ? `bg-terracotta hover:bg-terracottaDark text-bone`
                    : quizDone
                      ? `border border-bone/40 text-bone hover:bg-bone hover:text-forest`
                      : `border border-forest text-forest hover:bg-forest hover:text-bone`;
                  if (c.external) {
                    return (
                      <a
                        key={c.label}
                        href={c.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`quiz-cta-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-bold uppercase tracking-wider text-sm ${SOFT_HOVER} ${cls}`}
                      >
                        {c.label} <ArrowRight size={16} strokeWidth={2.5} />
                      </a>
                    );
                  }
                  return (
                    <Link
                      key={c.label}
                      to={c.to}
                      data-testid={`quiz-cta-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-bold uppercase tracking-wider text-sm ${SOFT_HOVER} ${cls}`}
                    >
                      {c.label} <ChevronRight size={16} strokeWidth={2.5} />
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* FREE GUIDE — LEAD MAGNET */}
      <section id="free-guide" data-testid="explore-lead-magnet" className="py-24 md:py-32 px-6 md:px-12 bg-surface">
        <div className="max-w-6xl mx-auto rounded-[2.5rem] bg-bone overflow-hidden grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-5 p-10 md:p-14 bg-forest text-bone flex flex-col justify-between gap-8">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-terracotta mb-4">Free Reflection Guide</p>
              <h2 className="font-serif font-black text-4xl md:text-5xl leading-tight">
                5 Days. <br />
                <em className="italic text-terracotta">Calmer mind.</em>
              </h2>
              <p className="mt-5 text-bone/85 leading-relaxed text-base md:text-lg">
                Short, kind, and practical. Ten minutes a day for five days. Just what helps.
              </p>
            </div>
            <img src={GIFT_ILLO} alt="" loading="lazy" className="rounded-2xl self-start max-w-[200px]" />
          </div>

          <div className="lg:col-span-7 p-10 md:p-14">
            {success ? (
              <div data-testid="explore-lead-success" className="h-full flex flex-col justify-center">
                <p className="text-xs font-bold uppercase tracking-[0.3em] text-terracotta mb-4">You're in</p>
                <h3 className="font-serif font-black text-3xl md:text-4xl leading-tight mb-4">Your guide is ready.</h3>
                <p className="text-forest/75 mb-6">Check your inbox — and your spam folder if it's playing hard to get.</p>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/lead-magnet/download`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="explore-lead-download-btn"
                  className={`inline-flex items-center gap-2 w-fit bg-terracotta hover:bg-terracottaDark text-bone rounded-full px-7 py-3.5 font-bold uppercase tracking-wider text-sm ${SOFT_HOVER}`}
                >
                  Access Your Free Reflection Guide <ArrowRight size={16} strokeWidth={2.5} />
                </a>
              </div>
            ) : (
              <form onSubmit={onLeadSubmit} data-testid="explore-lead-form" className="h-full flex flex-col justify-center">
                <h3 className="font-serif font-black text-3xl md:text-4xl leading-tight">Send it to me.</h3>
                <p className="mt-3 text-forest/75 mb-8 text-base">No spam. Unsubscribe anytime.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    required
                    type="text"
                    value={first}
                    onChange={(e) => setFirst(e.target.value)}
                    placeholder="First name"
                    data-testid="explore-lead-firstname"
                    className="bg-surface border border-forest/15 rounded-2xl px-5 py-4 text-forest placeholder:text-forest/40 focus:outline-none focus:border-forest transition-colors font-medium"
                  />
                  <input
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email address"
                    data-testid="explore-lead-email"
                    className="bg-surface border border-forest/15 rounded-2xl px-5 py-4 text-forest placeholder:text-forest/40 focus:outline-none focus:border-forest transition-colors font-medium"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="explore-lead-submit"
                  className={`mt-5 inline-flex items-center justify-center gap-2 bg-terracotta hover:bg-terracottaDark text-bone rounded-full px-7 py-4 font-bold uppercase tracking-wider text-sm ${SOFT_HOVER} disabled:opacity-60`}
                >
                  {submitting ? "Sending..." : (<>Download Your Free Gift <ArrowRight size={16} strokeWidth={2.5} /></>)}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* MEET THE TEAM */}
      <section className="py-24 md:py-32 px-6 md:px-12" data-testid="explore-team">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-terracotta mb-4">Meet the team</p>
            <h2 className="font-serif font-black tracking-tight text-4xl md:text-6xl leading-[0.95]">
              The humans behind <em className="italic text-terracotta">the work.</em>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
            {team.map((m) => (
              <article key={m.name} data-testid={`explore-team-${m.name.split(" ")[0].toLowerCase()}`} className={SOFT_HOVER}>
                <div className="rounded-[2rem] overflow-hidden aspect-[4/5] bg-surface mb-7 flex items-center justify-center">
                  <img
                    src={m.image}
                    alt={m.name}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      const sib = e.currentTarget.nextElementSibling;
                      if (sib) sib.style.display = "flex";
                    }}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="hidden w-full h-full items-center justify-center bg-surface text-forest/35">
                    <span className="font-serif font-black text-7xl">{m.name.split(" ").map(n => n[0]).join("")}</span>
                  </div>
                </div>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-terracotta">{m.role}</p>
                <h3 className="font-serif font-black text-3xl md:text-4xl leading-tight mt-2">{m.name}</h3>
                {m.creds && <p className="text-forest/60 text-sm font-bold uppercase tracking-wider mt-1">{m.creds}</p>}
                <p className="mt-4 text-forest/80 leading-relaxed">{m.blurb}</p>
              </article>
            ))}
          </div>

          <div className="mt-14 text-center">
            <Link
              to="/about"
              data-testid="explore-team-cta"
              className="inline-flex items-center gap-2 font-bold uppercase tracking-wider text-sm underline underline-offset-4 hover:text-terracotta"
            >
              Read the full team story <ArrowRight size={14} strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ ACCORDION */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-surface" data-testid="explore-faq">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-terracotta mb-4 inline-flex items-center gap-2">
              <HelpCircle size={14} strokeWidth={2.5} /> Questions, answered
            </p>
            <h2 className="font-serif font-black tracking-tight text-4xl md:text-6xl leading-[0.95]">
              Before you book, <em className="italic text-terracotta">the basics.</em>
            </h2>
          </div>

          <div>
            {faqs.map((f, i) => (
              <FAQItem
                key={i}
                idx={i}
                q={f.q}
                a={f.a}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/contact"
              data-testid="explore-faq-contact"
              className={`inline-flex items-center gap-2 bg-forest hover:bg-terracotta text-bone rounded-full px-7 py-3.5 font-bold uppercase tracking-wider text-sm ${SOFT_HOVER}`}
            >
              Ask us anything <ArrowRight size={16} strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* SOCIAL ROW */}
      <section className="py-20 md:py-24 px-6 md:px-12 bg-bone" data-testid="explore-social">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-terracotta mb-4">Find us elsewhere</p>
          <h3 className="font-serif font-black text-3xl md:text-5xl leading-tight tracking-tight mb-10">
            Therapy moments, <em className="italic text-terracotta">on your feed.</em>
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            {[
              { key: "tiktok", label: "TikTok", url: "https://www.tiktok.com/@7pillarsoflifetherapy", Icon: ArrowUpRight },
              { key: "youtube", label: "YouTube", url: "https://www.youtube.com/@7pillarsoflifetherapy", Icon: Play },
              { key: "instagram", label: "Instagram", url: "https://instagram.com/7pillarsoflifetherapy", Icon: Instagram },
              { key: "facebook", label: "Facebook", url: "https://www.facebook.com/7pillarsoflifetherapy", Icon: ArrowUpRight },
            ].map(({ key, label, url, Icon }) => (
              <a
                key={key}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`explore-social-${key}`}
                className={`group inline-flex items-center gap-3 bg-surface hover:bg-terracotta hover:text-bone rounded-full px-7 py-4 font-bold uppercase tracking-wider text-sm border border-forest/15 hover:border-terracotta ${SOFT_HOVER}`}
              >
                {label} <Icon size={16} strokeWidth={2.5} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
            ))}
          </div>
          <p className="mt-7 text-xs font-bold uppercase tracking-wider text-forest/55">@7pillarsoflifetherapy across all channels</p>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-bone" data-testid="explore-closing">
        <div className="max-w-5xl mx-auto relative">
          <div className="rounded-[2.5rem] bg-terracotta text-bone p-12 md:p-20 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full border border-bone/25" />
            <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full border border-bone/25" />
            <div className="absolute top-1/2 -right-8 w-32 h-32 rounded-full bg-bone/10" />
            <div className="relative">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-bone/80 mb-5">Begin today</p>
              <h2 className="font-serif font-black text-5xl md:text-7xl leading-[0.95] tracking-tight max-w-2xl">
                Ready when <em className="italic">you</em> are.
              </h2>
              <p className="mt-6 text-lg md:text-xl text-bone/90 max-w-xl leading-relaxed">
                A free 15-minute consult. No pressure. Just a conversation to see if we're the right fit.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="explore-closing-cta"
                  className={`inline-flex items-center gap-2 bg-bone text-forest rounded-full px-8 py-4 font-bold uppercase tracking-wider text-sm ${SOFT_HOVER} hover:bg-forest hover:text-bone`}
                >
                  Schedule Consultation <ArrowRight size={18} strokeWidth={2.5} />
                </a>
                <Link
                  to="/contact"
                  data-testid="explore-closing-contact"
                  className={`inline-flex items-center gap-2 bg-transparent border border-bone text-bone hover:bg-bone hover:text-forest rounded-full px-8 py-4 font-bold uppercase tracking-wider text-sm ${SOFT_HOVER}`}
                >
                  Send a message
                </Link>
                <Link
                  to="/blog"
                  data-testid="explore-closing-blog"
                  className={`inline-flex items-center gap-2 bg-transparent text-bone hover:underline underline-offset-4 px-2 py-4 font-bold uppercase tracking-wider text-sm transition-colors`}
                >
                  Read the blog <ArrowRight size={14} strokeWidth={2.5} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
